import { buildUrl, createRequest } from './common/httpUtils'

export type BizAccessLogEventType = 'login' | 'qr_tag_view' | 'api_access'

export type BizAccessLogSearchParams = {
  event_type?: BizAccessLogEventType
  user_id?: UserId | number
  path?: string
  start_at?: string
  end_at?: string
  page?: number
  per_page?: number
}

type BizAccessLogMeta = {
  total_pages?: number
  current_page?: number
}

type BizAccessLogResponse = {
  biz_access_logs?: IBizAccessLog[]
  meta?: BizAccessLogMeta
}

type BizAccessLogResult = {
  bizAccessLogs: IBizAccessLog[]
  totalPages?: number
  currentPage?: number
}

type HttpError = Error & { status?: number }

const buildHttpError = (message: string, status: number): HttpError => {
  const error = new Error(message) as HttpError
  error.status = status
  return error
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json()

  if (!response.ok) {
    const message = data?.error || data?.error_message || response.statusText
    throw buildHttpError(message, response.status)
  }

  return data as T
}

export const fetchBizAccessLogs = async (
  groupId: GroupId,
  params: BizAccessLogSearchParams = {},
): Promise<BizAccessLogResult> => {
  const searchParams = new URLSearchParams()
  if (params.event_type) searchParams.set('event_type', params.event_type)
  if (params.user_id) searchParams.set('user_id', String(params.user_id))
  if (params.path) searchParams.set('path', params.path)
  if (params.start_at) searchParams.set('start_at', params.start_at)
  if (params.end_at) searchParams.set('end_at', params.end_at)
  if (params.page) searchParams.set('page', String(params.page))
  if (params.per_page) searchParams.set('per_page', String(params.per_page))
  if (!searchParams.has('per_page')) searchParams.set('per_page', '50')

  const query = searchParams.toString()
  const url = buildUrl(`/api/v1/groups/${groupId}/biz_access_logs${query ? `?${query}` : ''}`)
  const request = createRequest(url, 'GET')
  const response = await fetch(request)
  const data = await handleResponse<BizAccessLogResponse>(response)

  const bizAccessLogs = Array.isArray(data.biz_access_logs) ? data.biz_access_logs : []

  return {
    bizAccessLogs,
    totalPages: data.meta?.total_pages,
    currentPage: data.meta?.current_page,
  }
}

export const updateBizAccessLogComment = async (
  groupId: GroupId,
  logId: number,
  comment: string | null,
): Promise<IBizAccessLog> => {
  const url = buildUrl(`/api/v1/groups/${groupId}/biz_access_logs/${logId}`)
  const request = createRequest(
    url,
    'PATCH',
    true,
    'application/json',
    {
      biz_access_log: {
        comment,
      },
    },
  )

  const response = await fetch(request)
  const data = await handleResponse<{ biz_access_log?: IBizAccessLog }>(response)

  if (data.biz_access_log) {
    return data.biz_access_log
  }

  return data as unknown as IBizAccessLog
}
